import { React, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import EditCategoryList from "../../core/modals/inventory/editcategorylist";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import {
  createCategory,
  getAllCategories,
  deleteCategory,
} from "../../api/categoryApi";
import { uploadCatalogImage } from "../../api/productApi";
import { toast } from "react-toastify";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";

const DEFAULT_CATEGORY_IMAGE = "/assets/img/by-default.png";

const CategoryList = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteCategories =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "categories");

  // const dataSource = useSelector(
  //   (state) => state.rootReducer.categotylist_data
  // );
  const [categoryEN, setCategoryEN] = useState("");
  const [categoryAR, setCategoryAR] = useState("");
  const [slugEN, setSlugEN] = useState("");
  const [slugAR, setSlugAR] = useState("");
  const [status, setStatus] = useState(true); // true = Active
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  // const [categoryImage, setCategoryImage] = useState("");
  const [categoryImagePreview, setCategoryImagePreview] = useState(null); // dataURL for UI only
  const [categoryImageKey, setCategoryImageKey] = useState("");

  // same idea as in AddProduct
  const normalizeKey = (s) => {
    if (!s) return "";
    let v = String(s).trim();
    v = v.replace(/^\/+/, ""); // remove leading slash
    v = v.replace(/^catelog\//i, "catalog/"); // fix legacy misspelling
    if (!/^catalog\//i.test(v)) {
      v = `catalog/${v.split("/").pop()}`;
    }
    return v;
  };

  // turn a key (or existing URL) into something <img src> can show
  const toPublicUrl = (v) => {
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v; // already a full URL

    const key = normalizeKey(v); // e.g. "catalog/CAT_category_20251111111657.png"
    const base = (
      process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
    ).replace(/\/+$/, "");
    // Example: base = "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"

    if (!base) {
      // fallback only if you serve /catalog/* from your app
      return `/${key}`;
    }
    return `${base}/${key}`;
  };

  // Add new category

  const handleAddCategory = async () => {
    const t = (s) => (window?.translate ? window.translate(s) : s);
    if (!canWriteCategories) {
      toast.error(`❌ ${t("You don't have permission to modify categories")}`);
      return;
    }

    const enName = categoryEN.trim();
    const arName = categoryAR.trim();
    const enSlug = slugEN.trim();
    const arSlug = slugAR.trim();

    if (!categoryImageKey) {
      toast.warn(t("Please upload a category image."));
      return;
    }

    try {
      const newCategory = {
        name: { en: enName, ar: arName },
        slug: { en: enSlug, ar: arSlug },
        image: normalizeKey(categoryImageKey),
        status: status ? "active" : "inactive",
      };

      await createCategory(newCategory);

      toast.success(` ${t("Category created successfully!")}`, {
        autoClose: 1200,
        closeOnClick: true,
        pauseOnHover: false,
        onClose: () => {
          // clean up form
          setCategoryEN("");
          setCategoryAR("");
          setSlugEN("");
          setSlugAR("");
          setCategoryImagePreview(null);
          setCategoryImageKey("");
          setStatus(true);

          // close modal (if open)
          document.getElementById("add-category")?.classList.remove("show");
          document.querySelector(".modal-backdrop")?.remove();
          document.body.classList.remove("modal-open");

          // now reload
          window.location.reload();
        },
      });
    } catch (error) {
      toast.error(`❌ ${t("Failed to create category")}`);
    }
  };

  // Upload category image
  const handleCategoryImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        // 1) show instant preview from local base64 (quick feedback)
        const dataUrl = reader.result;
        setCategoryImagePreview(dataUrl);

        // 2) build deterministic filename
        const ext =
          (file.type && file.type.split("/")[1]) ||
          file.name.split(".").pop() ||
          "jpg";
        const stamp = new Date()
          .toISOString()
          .replace(/[-:.TZ]/g, "")
          .slice(0, 14);
        const base = (slugEN || categoryEN || "category")
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-_]/g, "");
        const desiredName = `CAT_${base || "category"}_${stamp}.${ext}`;

        // 3) upload → backend returns { key: "catalog/xxx.png" }
        const { key } = await uploadCatalogImage(dataUrl, desiredName);

        // 4) store canonical key-only and swap preview to the public S3 URL
        const k = normalizeKey(key);
        setCategoryImageKey(k);
        setCategoryImagePreview(toPublicUrl(k)); // <-- preview now uses S3 URL
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setCategoryImagePreview(null);
        setCategoryImageKey("");
      }
    };
    reader.readAsDataURL(file);
  };

  //Get all categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getAllCategories();
        setCategoryList(data);
      } catch (err) {
        toast.error(`❌ ${window.translate("Failed to fetch categories")}`);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const refreshList = async () => {
    setLoading(true);
    try {
      const data = await getAllCategories();
      setCategoryList(data);
    } catch {
      toast.error(`❌ ${window.translate("Failed to fetch categories")}`);
    } finally {
      setLoading(false);
    }
  };

  //delete category
  const handleDeleteConfirm = async () => {
    if (!canWriteCategories) {
      toast.error(`❌ ${window.translate("You don't have permission to modify categories")}`);
      return;
    }
    if (!categoryToDelete) return;
    try {
      await deleteCategory(categoryToDelete._id);
      // toast.success("Category deleted successfully");
      toast.success(` ${window.translate("Category deleted successfully")}`);

      // ✅ Reload window after short delay to allow toast to show
      setTimeout(() => {
        window.location.reload();
      }, 800); // Optional: slight delay to ensure toast appears before reload
    } catch (error) {
      toast.error(`${window.translate("Failed to delete category")}`);
    } finally {
      setCategoryToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setCategoryToDelete(null);
    setDeleteModalOpen(false);
  };

  const columns = [
    {
      title: window.translate("Category"),
      dataIndex: "category",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <img
            src={record.image ? toPublicUrl(record.image) : DEFAULT_CATEGORY_IMAGE}
            alt="Category"
            style={{
              width: "40px",
              height: "40px",
              objectFit: "cover",
              borderRadius: "4px",
              marginRight: "10px",
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = DEFAULT_CATEGORY_IMAGE;
            }}
          />
          <span>
            {typeof record.category === "object"
              ? record.category.en
              : record.category}
          </span>
        </div>
      ),
      sorter: (a, b) => {
        const nameA =
          typeof a.category === "object" ? a.category.en : a.category;
        const nameB =
          typeof b.category === "object" ? b.category.en : b.category;
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: window.translate("Category Slug"),
      dataIndex: "categoryslug",
      sorter: (a, b) => a.categoryslug.length - b.categoryslug.length,
    },
    {
      title: window.translate("Created On"),
      dataIndex: "createdon",
      sorter: (a, b) => a.createdon.length - b.createdon.length,
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge fw-medium fs-10 ${
            text === "active" ? "bg-success" : "bg-danger"
          }`}
        >
          {text.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, c) =>
        canWriteCategories ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedCategory({
                    ...c,
                    imageKey: normalizeKey(c.image), // keep the original key for saving
                    imagePreview: toPublicUrl(c.image), // computed full URL for preview
                  });

                  // Delay to ensure state updates before opening modal
                  setTimeout(() => {
                    const modalElement = document.getElementById("edit-category");
                    const modal = new window.bootstrap.Modal(modalElement);
                    modal.show();
                  }, 100);
                }}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>

              <Link
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                to="#"
                onClick={() => {
                  setCategoryToDelete(c);
                  setDeleteModalOpen(true);
                }}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  const previewSrc =
    categoryImagePreview || selectedCategory?.imagePreview || "";

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Category")}</h4>
                <h6>{window.translate("Manage your categories")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteCategories && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-category"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i>
                  {window.translate("Add Category")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("sortLast7Days")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("recentlyAdded")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("ascending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("descending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("lastMonth")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("last7Days")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="table-responsive category-table">
              {loading ? (
                // 🔄 Spinner while categories are loading
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mb-0 mt-3 text-muted fw-medium">
                      {window.translate("Loading categories...")}
                    </p>
                  </div>
                </div>
              ) : categoryList.length > 0 ? (
                // ✅ Data loaded, show table
                <Table
                  columns={columns}
                  dataSource={categoryList.map((c) => ({
                    key: c._id,
                    _id: c._id || c.id,
                    category: c.name,
                    categoryslug: c.slug,
                    createdon: new Date(c.createdAt).toLocaleDateString(),
                    status: c.status,
                    image: c.image,
                  }))}
                />
              ) : (
                // 📝 No categories, centered message
                <div className="d-flex justify-content-center align-items-center py-5">
                  <p className="mb-0 text-muted fw-medium text-center">
                    {window.translate("No categories found.")}
                  </p>
                </div>
              )}
            </div>
          </div>
          {/* /product list */}
        </div>
        <CommonFooter />
      </div>

      {/* Add Category */}
      <div className="modal fade" id="add-category">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Add Category")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Category Image")}{" "}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleCategoryImageChange}
                      />
                      {previewSrc && (
                        <img
                          src={previewSrc}
                          alt="Preview"
                          style={{
                            width: 80,
                            height: 80,
                            marginTop: 10,
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Category")} (EN)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={categoryEN}
                        onChange={(e) => setCategoryEN(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Category")} (AR)
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={categoryAR}
                        onChange={(e) => setCategoryAR(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Category")}
                        {window.translate("Slug (EN)")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={slugEN}
                        onChange={(e) => setSlugEN(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Category")}{" "}
                        {window.translate("Slug (AR)")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={slugAR}
                        onChange={(e) => setSlugAR(e.target.value)}
                      />
                    </div>
                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("Status")}
                          <span className="text-danger ms-1">*</span>
                        </span>
                        <input
                          type="checkbox"
                          id="user2"
                          className="check"
                          defaultChecked
                          onChange={(e) => setStatus(e.target.checked)}
                          checked={status}
                        />
                        <label htmlFor="user2" className="checktoggle" />
                      </div>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <Link
                    to="#"
                    data-bs-dismiss="modal"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleAddCategory}
                  >
                    {window.translate("Add Category")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Add Category */}

      <EditCategoryList
        selectedCategory={selectedCategory}
        refreshList={refreshList}
      />
      <CommonDeleteModal
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={window.translate("Delete")}
      />
    </div>
  );
};

export default CategoryList;
